import pdfMake from 'addthaifont-pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';

dayjs.extend(buddhistEra);

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

const MONTH_TH = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน',
  'กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม'];
const DAY_TH = ['อา','จ','อ','พ','พฤ','ศ','ส'];

const LEAVE_ABBR = {
  SICK: 'ป่วย', PERSONAL: 'กิจ', VACATION: 'พร.',
  MATERNITY: 'คลอด', ORDINATION: 'บวช', UNPAID: 'ไม่รับ',
};

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtH = (h) => (Number.isInteger(h) ? String(h) : Number(h).toFixed(2));

// ── Colors ──────────────────────────────────────────────────────────────
const C = {
  headerBg: '#1e293b',
  headerText: '#FFFFFF',
  subHeaderBg: '#334155',
  infoLabelBg: '#f1f5f9',
  rowEven: '#f8fafc',
  rowOdd: '#FFFFFF',
  rowSun: '#fff7ed',
  rowSat: '#fff5f5',
  rowManual: '#eff6ff',
  rowLeave: '#f0fdf4',
  footerBg: '#e2e8f0',
  summaryIncome: '#065f46',
  summaryDeduct: '#dc2626',
  summaryTotal: '#0f172a',
  orange: '#f97316',
  blue: '#1d4ed8',
  green: '#15803d',
  muted: '#94a3b8',
  border: '#e2e8f0',
  borderDark: '#cbd5e1',
  late: '#f97316',
  nocheckout: '#ef4444',
  holiday: '#15803d',
  skip: '#7c3aed',
  hour: '#3b82f6',
  wage: '#b45309',
};

const noteText = (notes) => {
  if (!notes || notes.length === 0) return { text: '' };
  return {
    stack: notes.map((n) => {
      let color = C.muted;
      if (n.type === 'late') color = C.late;
      else if (n.type === 'nocheckout') color = C.nocheckout;
      else if (n.type === 'hour' || n.type === 'hour_wage') color = C.hour;
      else if (n.type === 'wage') color = C.wage;
      else if (n.type === 'holiday' || n.type === 'leave') color = C.holiday;
      else if (n.type === 'skip_late') color = C.skip;
      return { text: n.text, color, fontSize: 7, lineHeight: 1.3 };
    }),
  };
};

// ── helper: info row ─────────────────────────────────────────────────────
const infoRow = (label, value, accent = false) => [
  { text: label, fontSize: 8, color: '#64748b', fillColor: C.infoLabelBg, border: [false, false, false, false], margin: [0, 1] },
  { text: value || '—', fontSize: 9.5, bold: true, color: accent ? C.blue : C.summaryTotal, border: [false, false, false, false], margin: [0, 1] },
];

// ── helper: th cell ──────────────────────────────────────────────────────
const th = (text, align = 'center', opts = {}) => ({
  text, alignment: align, bold: true, fontSize: 8.5,
  fillColor: C.headerBg, color: C.headerText,
  border: [false, false, false, false],
  margin: [2, 3],
  ...opts,
});

// ── helper: td cell ──────────────────────────────────────────────────────
const td = (text, align = 'right', opts = {}) => ({
  text: text ?? '', alignment: align, fontSize: 8.5,
  border: [false, false, false, true],
  borderColor: [null, null, null, C.border],
  margin: [2, 2],
  ...opts,
});

// ── sum row item — hint = ค่าก่อนแก้ (แสดงเมื่อ HR override ด้วยมือ, ตรงกับ modal) ──
const sumRow = (label, value, color, bold = false, hint = null) => ({
  stack: [
    {
      columns: [
        { text: label, width: '*', fontSize: 9, color: '#374151', bold },
        { text: `฿${fmt(value)}`, width: 80, alignment: 'right', fontSize: 9, color, bold },
      ],
    },
    ...(hint != null
      ? [{ text: `(ก่อนแก้: ${fmt(hint)})`, alignment: 'right', fontSize: 7, color: C.muted }]
      : []),
  ],
  margin: [0, 1.5],
});

/**
 * @param {object} params
 * @param {object} params.emp         - employee row จาก timestamp.rows
 * @param {number} params.month       - 0-based month
 * @param {number} params.year        - CE year
 * @param {Array}  params.dailyRows   - pre-computed daily rows (from EmployeeSalaryReport)
 * @param {number} params.totalWage
 * @param {number} params.totalOt
 * @param {number} params.totalLateDed
 * @param {number} params.totalAllowanceDaily
 * @param {number} params.totalOtHours
 * @param {number} params.workDaysCount
 * @param {number} params.ssoDed
 * @param {number} params.taxDed
 * @param {number} params.otherDed
 * @param {number} params.allowances
 * @param {number} params.bonus
 * @param {number} params.net
 * @param {string} params.periodLabel
 * @param {number} params.totalOtRaw        - ค่า OT ก่อนแก้ (คำนวณสด) สำหรับ hint "(ก่อนแก้: X)"
 * @param {number} params.fieldAllowanceRaw - ค่าออกสนามก่อนแก้ (คำนวณสด) สำหรับ hint "(ก่อนแก้: X)"
 * @param {Array}  params.manualFields      - field ที่ HR แก้มือ (จาก payrollRow.manual_fields)
 */
const employeeSalaryReportPDF = ({
  emp, month, year,
  dailyRows,
  totalWage, totalOt, totalLateDed, totalAllowanceDaily, totalOtHours,
  workDaysCount,
  ssoDed, taxDed, otherDed, allowances, bonus, net, fieldAllowanceTotal,
  periodLabel, isWorkTime, totalActualWage,
  totalOtRaw = null, fieldAllowanceRaw = null, manualFields = [],
  mode = 'open',
}) => {
  const spd = emp.salary_per_day || 0;
  const spm = emp.salary_per_month || 0;
  const printedDate = `${dayjs().date()} ${MONTH_TH[dayjs().month()]} ${dayjs().year() + 543}`;
  const activeRows = dailyRows.filter((r) => !r.isEmpty);

  // ── Daily table body ─────────────────────────────────────────────────
  const dailyHeader = [
    th('วันที่', 'left'),
    th('เข้างาน'),
    th('ออกงาน'),
    th('โครงการ', 'left'),
    th('ชม.'),
    th('อัตรา'),
    th('ค่าแรง (฿)'),
    th('OT ชม.'),
    th('ค่า OT (฿)'),
    th('เบี้ยเลี้ยง (฿)'),
    th('ออกสนาม (฿)'),
    ...(isWorkTime ? [] : [th('หักสาย (฿)')]),
    th('รวม (฿)'),
  ];

  const dailyBody = dailyRows.map((r, i) => {
    const rowFill = r.isEmpty
      ? (r.isSun ? C.rowSun : r.isSat ? C.rowSat : i % 2 === 0 ? C.rowEven : C.rowOdd)
      : r.isLeaveOnly ? C.rowLeave
      : r.isSun ? C.rowSun
      : r.isSat ? C.rowSat
      : r.isManual ? C.rowManual
      : i % 2 === 0 ? C.rowEven : C.rowOdd;

    const dateColor = (r.isSun || r.isSat) ? '#dc2626' : '#374151';

    const dispH = isWorkTime ? r.actualH || 0 : r.hours;
    const dispWage = isWorkTime ? r.actualWage || 0 : r.wage;
    const dispTotal = isWorkTime
      ? dispWage + r.otAmt + r.allowance + (r.fieldAllowance || 0)
      : dispWage + r.otAmt + r.allowance + (r.fieldAllowance || 0) - r.lateDed;

    if (r.isEmpty) {
      return [
        td(`${DAY_TH[r.dow]} ${r.d}/${month + 1}`, 'left', { color: dateColor, fillColor: rowFill, fontSize: 8 }),
        td('', 'center', { fillColor: rowFill, colSpan: isWorkTime ? 11 : 12, color: C.muted, text: '—', alignment: 'center' }),
        ...Array.from({ length: isWorkTime ? 10 : 11 }).map(() => ({})),
      ];
    }

    const row = [
      td(`${DAY_TH[r.dow]} ${r.d}/${month + 1}`, 'left', { color: dateColor, fillColor: rowFill, fontSize: 8 }),
      td(r.checkInTime || '—', 'center', { fillColor: rowFill, fontSize: 8 }),
      td(r.checkOutTime || '—', 'center', { fillColor: rowFill, fontSize: 8 }),
      td(r.project || '—', 'left', { fillColor: rowFill, fontSize: 7, color: '#475569' }),
      td(fmtH(dispH), 'center', { fillColor: rowFill }),
      td(`×${r.er}`, 'center', { fillColor: rowFill, color: r.er > 1 ? C.green : '#374151' }),
      td(fmt(dispWage), 'right', { fillColor: rowFill, bold: true }),
      td(
        r.otHours > 0
          ? `${fmtH(r.otHours)} ชม. (×${r.otRate})${r.isManualOt ? ' ✎' : ''}`
          : '—',
        'center',
        { fillColor: rowFill, color: r.otHours > 0 ? '#ea580c' : C.muted },
      ),
      td(r.otAmt > 0 ? fmt(r.otAmt) : '—', 'right', { fillColor: rowFill, bold: r.otAmt > 0, color: r.otAmt > 0 ? '#ea580c' : C.muted }),
      td(r.allowance > 0 ? fmt(r.allowance) : '—', 'right', { fillColor: rowFill, color: r.allowance > 0 ? C.blue : C.muted }),
      td(r.fieldAllowance > 0 ? fmt(r.fieldAllowance) : '—', 'right', { fillColor: rowFill, color: r.fieldAllowance > 0 ? C.blue : C.muted }),
    ];
    if (!isWorkTime) {
      row.push(td(r.lateDed > 0 ? `−${fmt(r.lateDed)}` : '—', 'right', { fillColor: rowFill, color: r.lateDed > 0 ? C.orange : C.muted }));
    }
    row.push(td(fmt(dispTotal), 'right', { fillColor: rowFill, bold: true, color: C.summaryIncome }));
    return row;
  });

  const footerWage = isWorkTime ? totalActualWage : totalWage;
  const footerTotal = isWorkTime
    ? footerWage + totalOt + totalAllowanceDaily + (fieldAllowanceTotal || 0)
    : footerWage + totalOt + totalAllowanceDaily + (fieldAllowanceTotal || 0) - totalLateDed;

  const dailyFooter = [
    td(`รวม (${activeRows.length} วันทำงาน)`, 'left', { bold: true, fillColor: C.footerBg, colSpan: 6 }),
    {}, {}, {}, {}, {},
    td(fmt(footerWage), 'right', { bold: true, fillColor: C.footerBg }),
    td(totalOtHours > 0 ? `${fmtH(totalOtHours)} ชม.` : '—', 'center', { bold: true, fillColor: C.footerBg, color: totalOtHours > 0 ? '#ea580c' : C.muted }),
    td(totalOt > 0 ? fmt(totalOt) : '—', 'right', { bold: true, fillColor: C.footerBg, color: totalOt > 0 ? '#ea580c' : C.muted }),
    td(totalAllowanceDaily > 0 ? fmt(totalAllowanceDaily) : '—', 'right', { bold: true, fillColor: C.footerBg, color: totalAllowanceDaily > 0 ? C.blue : C.muted }),
    td(fieldAllowanceTotal > 0 ? fmt(fieldAllowanceTotal) : '—', 'right', { bold: true, fillColor: C.footerBg, color: fieldAllowanceTotal > 0 ? C.blue : C.muted }),
  ];
  if (!isWorkTime) {
    dailyFooter.push(td(totalLateDed > 0 ? `−${fmt(totalLateDed)}` : '—', 'right', { bold: true, fillColor: C.footerBg, color: totalLateDed > 0 ? C.orange : C.muted }));
  }
  dailyFooter.push(td(fmt(footerTotal), 'right', { bold: true, fillColor: C.footerBg, color: C.summaryIncome }));

  const otSection = [];

  // ── Summary ───────────────────────────────────────────────────────────
  // แสดงทุกบรรทัดเสมอ (สีเทาถ้าเป็น 0) — ตรงกับ modal, ไม่ซ่อนแถวเมื่อค่า = 0
  const otOverridden =
    manualFields.includes('overtime') &&
    totalOtRaw != null &&
    Math.abs(totalOt - totalOtRaw) > 0.005;
  const faOverridden =
    manualFields.includes('field_allowance') &&
    fieldAllowanceRaw != null &&
    Math.abs((fieldAllowanceTotal || 0) - fieldAllowanceRaw) > 0.005;

  const summaryItems = [];
  summaryItems.push(sumRow('ค่าแรง', footerWage, C.summaryTotal));
  summaryItems.push(
    sumRow(
      totalOtHours > 0 ? `ค่า OT (${fmtH(totalOtHours)} ชม.)` : 'ค่า OT',
      totalOt,
      totalOt > 0 ? C.orange : C.muted,
      false,
      otOverridden ? totalOtRaw : null,
    ),
  );
  summaryItems.push(sumRow('เบี้ยเลี้ยง', allowances, allowances > 0 ? C.blue : C.muted));
  summaryItems.push(
    sumRow(
      'ค่าออกสนาม',
      fieldAllowanceTotal,
      fieldAllowanceTotal > 0 ? C.green : C.muted,
      false,
      faOverridden ? fieldAllowanceRaw : null,
    ),
  );
  summaryItems.push(sumRow('โบนัส / เงินได้อื่นๆ', bonus, bonus > 0 ? C.green : C.muted));
  summaryItems.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 0.5, dash: { length: 3 }, color: C.borderDark }], margin: [0, 3] });
  if (!isWorkTime) {
    summaryItems.push(sumRow('หักมาสาย', totalLateDed, totalLateDed > 0 ? C.summaryDeduct : C.muted));
  }
  summaryItems.push(sumRow('หักประกันสังคม', ssoDed, ssoDed > 0 ? '#64748b' : C.muted));
  summaryItems.push(sumRow('หักภาษีเงินได้', taxDed, taxDed > 0 ? '#64748b' : C.muted));
  summaryItems.push(sumRow('หักอื่นๆ', otherDed, otherDed > 0 ? '#64748b' : C.muted));
  summaryItems.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 1.5, color: C.summaryTotal }], margin: [0, 4] });
  summaryItems.push(sumRow('ยอดสุทธิที่ได้รับ', net, net >= 0 ? C.summaryIncome : C.summaryDeduct, true));

  // ── Document definition ───────────────────────────────────────────────
  const docDef = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [30, 60, 30, 45],
    defaultStyle: { font: 'Sarabun', fontSize: 9 },
    info: {
      title: `ใบสรุปค่าแรง_${emp.name}_${periodLabel}`.replace(/\s+/g, '_'),
      subject: periodLabel,
    },

    header: () => ({
      margin: [30, 14, 30, 0],
      columns: [
        {
          stack: [
            { text: 'CorePlan ERP', fontSize: 14, bold: true, color: C.summaryTotal },
            { text: 'ใบสรุปค่าแรงประจำงวด  /  Monthly Wage Summary', fontSize: 9, color: '#64748b', margin: [0, 1] },
          ],
          width: '*',
        },
        {
          stack: [
            { text: `งวดวันที่: ${periodLabel}`, alignment: 'right', fontSize: 9, bold: true },
            { text: `วันที่พิมพ์: ${printedDate}`, alignment: 'right', fontSize: 8.5, color: '#64748b' },
          ],
          width: 'auto',
        },
      ],
    }),

    footer: (currentPage, pageCount) => ({
      margin: [30, 0],
      columns: [
        { text: 'เอกสารนี้จัดทำโดยระบบ CorePlan ERP — ข้อมูลในเอกสารฉบับนี้เป็นความลับ', fontSize: 7.5, color: C.muted },
        { text: `หน้า ${currentPage} / ${pageCount}`, alignment: 'right', fontSize: 7.5, color: C.muted },
      ],
    }),

    content: [
      // ── Employee info table ──────────────────────────────────────────
      {
        table: {
          widths: ['18%', '32%', '18%', '32%'],
          body: [
            [
              ...infoRow('ชื่อ - นามสกุล', emp.name),
              ...infoRow('รหัสพนักงาน', emp.emp_code),
            ],
            [
              ...infoRow('ตำแหน่ง', emp.role),
              ...infoRow('แผนก', emp.department),
            ],
            [
              ...infoRow('ประเภทพนักงาน', emp.emp_type === 'FULLTIME' ? 'รายเดือน' : 'รายวัน'),
              ...infoRow('อัตราค่าจ้าง',
                (spd > 0 ? `฿${fmt(spd)}/วัน` : '') + (spm > 0 ? `  (฿${fmt(spm)}/เดือน)` : ''),
                true),
            ],
            [
              ...infoRow('วันทำงานในงวดนี้', `${workDaysCount} วัน`),
              ...infoRow('ชั่วโมง OT รวม', totalOtHours > 0 ? `${fmtH(totalOtHours)} ชม.` : '—'),
            ],
          ],
        },
        layout: {
          fillColor: (rowIndex) => (rowIndex % 2 === 0 ? '#f8fafc' : '#FFFFFF'),
          hLineWidth: () => 0.5,
          vLineWidth: () => 0,
          hLineColor: () => C.border,
        },
        margin: [0, 0, 0, 10],
      },

      // ── Daily wage table ─────────────────────────────────────────────
      { text: 'รายละเอียดค่าแรงรายวัน', style: 'sectionTitle', margin: [0, 0, 0, 3] },
      {
        table: {
          widths: isWorkTime
            ? ['9%', '6%', '6%', '*', '5%', '5%', '9%', '7%', '8%', '7%', '7%', '9%']
            : ['9%', '6%', '6%', '*', '5%', '4%', '8%', '6%', '7%', '7%', '7%', '6%', '8%'],
          headerRows: 1,
          body: [dailyHeader, ...dailyBody, dailyFooter],
        },
        layout: {
          defaultBorder: false,
          hLineWidth: (i, node) => (i === node.table.body.length ? 0 : 0),
        },
      },

      // ── OT table ─────────────────────────────────────────────────────
      ...otSection,

      // ── Summary ───────────────────────────────────────────────────────
      { text: 'สรุปยอดรวมประจำงวด', style: 'sectionTitle', margin: [0, 10, 0, 3] },
      {
        columns: [
          { width: '*', text: '' },
          {
            width: 220,
            stack: [
              {
                table: {
                  widths: ['*', 90],
                  body: [[
                    { text: 'รายการ', bold: true, fillColor: C.headerBg, color: '#fff', fontSize: 8.5, alignment: 'left', border: [false, false, false, false], margin: [4, 3] },
                    { text: 'จำนวนเงิน (฿)', bold: true, fillColor: C.headerBg, color: '#fff', fontSize: 8.5, alignment: 'right', border: [false, false, false, false], margin: [4, 3] },
                  ]],
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 2],
              },
              ...summaryItems,
            ],
          },
        ],
      },

      // ── Signature section ─────────────────────────────────────────────
      { text: '', margin: [0, 20] },
      {
        columns: [
          {
            stack: [
              { canvas: [{ type: 'line', x1: 10, y1: 0, x2: 120, y2: 0, lineWidth: 0.8, color: '#475569' }] },
              { text: 'ผู้รับเงิน', alignment: 'center', fontSize: 9, color: '#475569', margin: [0, 3] },
              { text: emp.name, alignment: 'center', fontSize: 9, bold: true, margin: [0, 1] },
              { text: 'วันที่ ......../......../........', alignment: 'center', fontSize: 8.5, color: '#64748b', margin: [0, 4] },
            ],
          },
          {
            stack: [
              { canvas: [{ type: 'line', x1: 10, y1: 0, x2: 120, y2: 0, lineWidth: 0.8, color: '#475569' }] },
              { text: 'ฝ่ายบุคคล / ผู้จัดทำ', alignment: 'center', fontSize: 9, color: '#475569', margin: [0, 3] },
              { text: ' ', margin: [0, 1] },
              { text: 'วันที่ ......../......../........', alignment: 'center', fontSize: 8.5, color: '#64748b', margin: [0, 4] },
            ],
          },
          {
            stack: [
              { canvas: [{ type: 'line', x1: 10, y1: 0, x2: 120, y2: 0, lineWidth: 0.8, color: '#475569' }] },
              { text: 'ผู้อนุมัติ', alignment: 'center', fontSize: 9, color: '#475569', margin: [0, 3] },
              { text: ' ', margin: [0, 1] },
              { text: 'วันที่ ......../......../........', alignment: 'center', fontSize: 8.5, color: '#64748b', margin: [0, 4] },
            ],
          },
        ],
        columnGap: 20,
      },
    ],

    styles: {
      sectionTitle: {
        fontSize: 10,
        bold: true,
        color: C.summaryTotal,
      },
    },
  };

  const filename = `ใบสรุปค่าแรง_${emp.name}_${periodLabel}.pdf`.replace(/\s+/g, '_');
  if (mode === 'download') {
    pdfMake.createPdf(docDef).download(filename);
  } else {
    pdfMake.createPdf(docDef).open();
  }
};

export default employeeSalaryReportPDF;
